import { type RaceWithParticipants } from "@/backend/domain/race/getRace";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { formatRaceTime } from "@/lib/utils";
import { ParticipantTable } from '../participants/participantTable';

type ViewMode = 'class' | 'gender' | 'team';

interface ResultsViewProps {
    race: RaceWithParticipants;
    searchTerm: string;
    viewMode: ViewMode;
}

interface TeamResult {
    name: string;
    members: RaceWithParticipants['participants'];
    totalTime: number;
    isChildrenTeam: boolean;
}

const PENALTY_TIME = 180; // 3 minutes in seconds

function isChildrenTeam(members: RaceWithParticipants['participants'], raceDate: string): boolean {
    const raceYear = new Date(raceDate).getFullYear();
    return members.every(member => {
        const age = raceYear - member.birthYear;
        return age < 16;
    });
}

function calculateTeamResults(race: RaceWithParticipants): TeamResult[] {
    // Group participants by team
    const teamGroups = race.participants.reduce((groups, participant) => {
        if (!participant.teamName) return groups;

        if (!groups[participant.teamName]) {
            groups[participant.teamName] = [];
        }
        groups[participant.teamName].push(participant);
        return groups;
    }, {} as Record<string, RaceWithParticipants['participants']>);

    // Calculate team results
    return Object.entries(teamGroups).map(([teamName, members]) => {
        const totalTime = members.reduce((sum, member) => {
            return sum + (member.raceTime || PENALTY_TIME);
        }, 0);

        return {
            name: teamName,
            members,
            totalTime,
            isChildrenTeam: isChildrenTeam(members, race.date)
        };
    });
}

function ResultsByClass({ race, searchTerm }: ResultsViewProps) {
    // Group participants by class
    const participantsByClass = race.participants.reduce((acc, participant) => {
        const className = participant.raceClass || 'Ohne Klasse';
        if (!acc[className]) {
            acc[className] = [];
        }
        acc[className].push(participant);
        return acc;
    }, {} as Record<string, typeof race.participants>);

    // Sort classes by start number to maintain consistent order
    const sortedClasses = Object.keys(participantsByClass).sort((a, b) => {
        if (a === 'Ohne Klasse') return 1;
        if (b === 'Ohne Klasse') return -1;
        return (participantsByClass[a][0].startNumber || 0) - (participantsByClass[b][0].startNumber || 0);
    });

    return (
        <div className="space-y-8">
            {sortedClasses.map(className => (
                <Card key={className}>
                    <CardHeader>
                        <CardTitle className="text-lg font-light tracking-wide">
                            {className}
                        </CardTitle>
                    </CardHeader>
                    <CardContent>
                        <ParticipantTable
                            participants={participantsByClass[className].filter(p =>
                                p.firstName.toLowerCase().includes(searchTerm.toLowerCase()) ||
                                p.lastName.toLowerCase().includes(searchTerm.toLowerCase())
                            )}
                            showResults
                            groupByClass
                            initialSort="raceTime"
                            showPlaceOfLiving={race.ask_for_place_of_living}
                        />
                    </CardContent>
                </Card>
            ))}
        </div>
    );
}

function ResultsByGender({ race, searchTerm }: ResultsViewProps) {
    const participantsByGender = race.participants.reduce((acc, participant) => {
        const gender = participant.gender === 'female' ? 'Weiblich' : 'Männlich';
        if (!acc[gender]) {
            acc[gender] = [];
        }
        acc[gender].push(participant);
        return acc;
    }, {} as Record<string, typeof race.participants>);

    return (
        <div className="space-y-8">
            {Object.entries(participantsByGender).map(([gender, participants]) => (
                <Card key={gender}>
                    <CardHeader>
                        <CardTitle className="text-lg font-light tracking-wide">
                            {gender}
                        </CardTitle>
                    </CardHeader>
                    <CardContent>
                        <ParticipantTable
                            participants={participants.filter(p =>
                                p.firstName.toLowerCase().includes(searchTerm.toLowerCase()) ||
                                p.lastName.toLowerCase().includes(searchTerm.toLowerCase())
                            )}
                            showResults
                            hideGender
                            groupByGender
                            initialSort="raceTime"
                            showPlaceOfLiving={race.ask_for_place_of_living}
                        />
                    </CardContent>
                </Card>
            ))}
        </div>
    );
}

function TeamResults({ race, searchTerm }: ResultsViewProps) {
    const teamResults = calculateTeamResults(race);

    const childrenTeams = teamResults
        .filter(team => team.isChildrenTeam)
        .sort((a, b) => a.totalTime - b.totalTime);

    const adultTeams = teamResults
        .filter(team => !team.isChildrenTeam)
        .sort((a, b) => a.totalTime - b.totalTime);

    function renderTeamSection(teams: TeamResult[], title: string) {
        return teams.length > 0 && (
            <div className="space-y-4">
                <h3 className="text-xl font-semibold">{title}</h3>
                {teams.map((team, index) => (
                    <Card key={team.name}>
                        <CardHeader>
                            <div className="flex justify-between items-baseline">
                                <CardTitle className="text-lg font-light tracking-wide">
                                    {team.name}
                                </CardTitle>
                                <div className="text-sm text-muted-foreground">
                                    Platz {index + 1} - Gesamtzeit: {formatRaceTime(team.totalTime)}
                                </div>
                            </div>
                        </CardHeader>
                        <CardContent>
                            <ParticipantTable
                                participants={team.members.filter(p =>
                                    p.firstName.toLowerCase().includes(searchTerm.toLowerCase()) ||
                                    p.lastName.toLowerCase().includes(searchTerm.toLowerCase())
                                )}
                                showResults
                                isTeamMemberList
                                initialSort="raceTime"
                                showPlaceOfLiving={race.ask_for_place_of_living}
                            />
                        </CardContent>
                    </Card>
                ))}
            </div>
        );
    }

    return (
        <div className="space-y-8">
            {renderTeamSection(childrenTeams, "Kinder Mannschaften")}
            {renderTeamSection(adultTeams, "Erwachsenen Mannschaften")}
        </div>
    );
}

export function ResultsView({ race, searchTerm, viewMode }: ResultsViewProps) {
    // Render different views based on viewMode
    if (viewMode === 'class') {
        return <ResultsByClass race={race} searchTerm={searchTerm} viewMode={viewMode} />;
    }

    if (viewMode === 'gender') {
        return <ResultsByGender race={race} searchTerm={searchTerm} viewMode={viewMode}/>;
    }

    if (viewMode === 'team') {
        return <TeamResults race={race} searchTerm={searchTerm} viewMode={viewMode}/>;
    }

    return null;
}
